// Highcharts conventions shared by the Energy timeline and the Temp / Vent history charts: Tallinn time,
// stacked panes with small y labels, no floating tooltip (a pinned readout driven by hover / touch instead),
// drag / pinch zoom with our own "Reset zoom", pane titles and a crosshair drawn as HTML over the chart.
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { HAIRLINE, INK, MUTED, SECONDARY } from './tokens';

// A chart that was replaced (remounted with other panes) is destroyed, which strips its properties; state can
// still hold it for a render.
const usable = (chart: Highcharts.Chart | null): chart is Highcharts.Chart => !!chart?.xAxis?.[0];

// chart.update(options, redraw, oneToOne): series and axes are matched by id.
export const UPDATE_ARGS: [boolean, boolean, boolean] = [true, true, false];

export const TALLINN_TIME: Highcharts.TimeOptions = { timezone: 'Europe/Tallinn' };

// chart.events.load: our own "Reset zoom" button replaces Highcharts' one.
export function hideHighchartsResetZoom(this: Highcharts.Chart): void {
  Highcharts.addEvent(this, 'beforeShowResetZoom', (e: Event) => e.preventDefault());
}

// chart.events.render: labels of ticks on a pane's edge go inside it, so they don't collide with the next
// pane's (except on axes `keep` returns true for).
export function nudgeEdgeLabels(chart: Highcharts.Chart, font: number, keep?: (axis: Highcharts.Axis) => boolean) {
  const nudge = font * 0.5;
  for (const axis of chart.yAxis) {
    for (const tick of Object.values(axis.ticks)) {
      const at = (v: number | null | undefined) => typeof v === 'number' && Math.abs(tick.pos - v) < 1e-9;
      const top = at(axis.max) && !keep?.(axis);
      tick.label?.attr({ translateY: top ? nudge : at(axis.min) ? -nudge : 0 });
    }
  }
}

// A y axis occupying one pane of the stacked chart, with small labels left of the plot.
export function paneAxis(pane: { top: number; height: number }, font: number): Highcharts.YAxisOptions {
  return {
    top: pane.top,
    height: pane.height,
    offset: 0,
    title: { text: undefined },
    gridLineColor: HAIRLINE,
    lineWidth: 0,
    startOnTick: false,
    endOnTick: false,
    labels: {
      align: 'right',
      x: -5,
      reserveSpace: false,
      allowOverlap: true,
      style: { fontSize: `${font}px`, color: MUTED, textOverflow: 'none', whiteSpace: 'nowrap' },
    },
  };
}

// CO2 ticks: 400, the thresholds and the top; "1k" / "1.2k" where space is short.
export const ppmLabel = (v: number, compact: boolean) => (compact && v >= 1000 ? `${v / 1000}k` : String(v));

// Level axis labels (1..3).
export const LEVEL_LABELS = ['', 'low', 'mid', 'high'];

export const ChartView = React.memo(function ChartView(props: {
  options: Highcharts.Options;
  onChart: (chart: Highcharts.Chart) => void;
  updateArgs?: [boolean, boolean, boolean];
}) {
  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={props.options}
      updateArgs={props.updateArgs ?? UPDATE_ARGS}
      callback={props.onChart}
    />
  );
});

// The x under the pointer (epoch ms) from mouse / touch over the chart's wrapper. A touch has no "leave":
// the value is kept a moment after lifting the finger so it can be read.
export function useChartPointer(chart: Highcharts.Chart | null) {
  const [sel, setSel] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const touchTimer = useRef<number | undefined>(undefined);
  const pointAt = (clientX: number) => {
    const wrap = wrapRef.current;
    if (!usable(chart) || !wrap) {
      return;
    }
    const x = clientX - wrap.getBoundingClientRect().left;
    setSel(x < chart.plotLeft || x > chart.plotLeft + chart.plotWidth ? null : chart.xAxis[0].toValue(x));
  };
  const onTouch = (e: React.TouchEvent) => {
    window.clearTimeout(touchTimer.current);
    if (e.touches.length === 1) {
      pointAt(e.touches[0].clientX);
    }
  };
  useEffect(() => () => window.clearTimeout(touchTimer.current), []);
  return {
    sel,
    setSel,
    wrapRef,
    handlers: {
      onMouseMove: (e: React.MouseEvent) => pointAt(e.clientX),
      onMouseLeave: () => setSel(null),
      onTouchStart: onTouch,
      onTouchMove: onTouch,
      onTouchEnd: () => {
        window.clearTimeout(touchTimer.current);
        touchTimer.current = window.setTimeout(() => setSel(null), 4000);
      },
    },
  };
}

export interface ZoomHandlers {
  onExtremes: (min: number, max: number) => void;
}

// Keeps the x axis on `win` (a range pick, a zoom reset, and as "now" moves) unless the user zoomed by
// drag-select or pinch; any other extremes count as a zoom. Pass `handlers` to the options'
// xAxis.events.afterSetExtremes.
export function useWindowZoom(chart: Highcharts.Chart | null, win: [number, number], onChange: () => void) {
  const [zoom, setZoom] = useState<[number, number] | null>(null);
  const winRef = useRef(win);
  winRef.current = win;
  const handlers = useRef<ZoomHandlers>({ onExtremes: () => undefined });
  handlers.current.onExtremes = (min, max) => {
    const [w0, w1] = winRef.current;
    const zoomed = Math.abs(min - w0) > 60_000 || Math.abs(max - w1) > 60_000;
    setZoom(zoomed ? [min, max] : null);
    onChange();
  };
  useEffect(() => {
    if (!usable(chart) || zoom) {
      return;
    }
    const axis = chart.xAxis[0];
    if (axis.min !== win[0] || axis.max !== win[1]) {
      axis.setExtremes(win[0], win[1], true, false);
    }
  }, [chart, zoom, win[0], win[1]]);
  const resetZoom = useCallback(() => setZoom(null), []);
  return { zoom, resetZoom, handlers, winRef };
}

// Small pane title inside the pane's top-left corner.
export function PaneTitle(props: {
  left: number;
  top: number;
  font: number;
  web?: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        position: 'absolute',
        left: props.left + (props.web ? 4 : 2),
        top: props.top + (props.web ? 2 : 0),
        fontSize: props.font,
        lineHeight: 1.3,
        color: SECONDARY,
        background: 'rgba(252,252,251,0.85)',
        padding: props.web ? '0 4px' : '0 3px',
        borderRadius: 3,
        pointerEvents: 'none',
      }}
    >
      {props.children}
    </span>
  );
}

// 1 px line at the pointer, across all panes.
export function Crosshair(props: { chart: Highcharts.Chart | null; x: number | null }) {
  const { chart, x } = props;
  if (!usable(chart) || x === null) {
    return null;
  }
  return (
    <div
      style={{
        position: 'absolute',
        top: chart.plotTop,
        height: chart.plotHeight,
        left: chart.xAxis[0].toPixels(x, false),
        width: 1,
        background: INK,
        pointerEvents: 'none',
      }}
    />
  );
}
